import "./AdManagement.scss";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import CurrentAdvertising from "./CurrentAdvertising/CurrentAdvertising";
import PastAdvertising from "./PastAdvertising/PastAdvertising";
import Button from "../../components/Buttons/Button";
import Skeleton from "../../components/Skeleton/Skeleton";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {Tab, TabsContainer} from '../../utils/tab';

function AdManagement() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('current');
    const [adStats, setAdStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [pendingAds, setPendingAds] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);

    const fetchPendingAds = useCallback(async () => {
        setPendingLoading(true);
        try {
            const { data } = await api.get('/ads/pending-review');
            setPendingAds(data.data || []);
        } catch (error) {
            console.error('Error fetching pending ads:', error);
        } finally {
            setPendingLoading(false);
        }
    }, []);

    useEffect(() => { fetchPendingAds(); }, [fetchPendingAds]);

    const handleApprove = async (adId) => {
        try {
            await api.post(`/ads/${adId}/approve`);
            toast.success('Ad approved.');
            fetchPendingAds();
        } catch (error) {
            toast.error('Failed to approve ad');
        }
    };

    const handleReject = async (adId) => {
        if (!window.confirm('Reject this ad? It will be deleted.')) return;
        try {
            await api.post(`/ads/${adId}/reject`);
            toast.success('Ad rejected');
            fetchPendingAds();
        } catch (error) {
            toast.error('Failed to reject ad');
        }
    };

    const fetchAdStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const { data } = await api.get('/ads/analytics', { params: { days: 30 } });
            setAdStats(data);
        } catch (error) {
            console.error('Error fetching ad analytics:', error);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    useEffect(() => { fetchAdStats(); }, [fetchAdStats]);

    const totalImpressions = adStats?.data?.reduce((sum, a) => sum + a.impressions, 0) || 0;
    const totalClicks = adStats?.data?.reduce((sum, a) => sum + a.clicks, 0) || 0;
    const totalDismissals = adStats?.data?.reduce((sum, a) => sum + a.dismissals, 0) || 0;
    const overallCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0';

    const formatDate = (ts) => {
        if (!ts) return '—';
        return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <main>
            <TopNav />
            <div className="main-container">
                <SideBar />
                <article className="page-content">
                    <section className="ad-management">
                        <div className="ad-management__top flex-wrap gap-2 pb-3">
                            <h1 className="ad-management__top-heading">Ad Management</h1>
                            <div style={{ display: "flex", gap: "20px" }}>
                                <Button
                                    label="Create new Ad +"
                                    isLink primaryBtn
                                    to={'/ad-management/add'}
                                />
                            </div>
                        </div>

                        {/* Ad Performance Stats */}
                        <div className="ad-stats">
                            <div className="ad-stats__label">Last 30 days</div>
                            {statsLoading ? (
                                <div className="ad-stats__grid">
                                    <Skeleton type="card" />
                                    <Skeleton type="card" />
                                    <Skeleton type="card" />
                                    <Skeleton type="card" />
                                </div>
                            ) : (
                                <div className="ad-stats__grid">
                                    <div className="ad-stats__card">
                                        <div className="ad-stats__number">{totalImpressions.toLocaleString()}</div>
                                        <div className="ad-stats__title">Impressions</div>
                                    </div>
                                    <div className="ad-stats__card">
                                        <div className="ad-stats__number">{totalClicks.toLocaleString()}</div>
                                        <div className="ad-stats__title">Clicks</div>
                                    </div>
                                    <div className="ad-stats__card">
                                        <div className="ad-stats__number">{overallCtr}%</div>
                                        <div className="ad-stats__title">Click Rate</div>
                                    </div>
                                    <div className="ad-stats__card">
                                        <div className="ad-stats__number">{totalDismissals.toLocaleString()}</div>
                                        <div className="ad-stats__title">Dismissed</div>
                                    </div>
                                </div>
                            )}

                            {/* Per-ad breakdown */}
                            {!statsLoading && adStats?.data?.length > 0 && (
                                <div className="ad-stats__breakdown">
                                    {adStats.data.filter(a => a.impressions > 0).map((ad) => (
                                        <div key={ad._id} className="ad-stats__row">
                                            <div className="ad-stats__row-name">{ad.business_name || 'Untitled'}</div>
                                            <div
                                                className="ad-stats__row-stat"
                                                style={{ cursor: ad.impressions > 0 ? 'pointer' : 'default', textDecoration: ad.impressions > 0 ? 'underline' : 'none' }}
                                                onClick={() => ad.impressions > 0 && navigate(`/ad-management/engaged?ad_id=${ad._id}&event_type=impression&name=${encodeURIComponent(ad.business_name || '')}`)}
                                            >
                                                {ad.impressions} views
                                            </div>
                                            <div
                                                className="ad-stats__row-stat"
                                                style={{ cursor: ad.clicks > 0 ? 'pointer' : 'default', textDecoration: ad.clicks > 0 ? 'underline' : 'none' }}
                                                onClick={() => ad.clicks > 0 && navigate(`/ad-management/engaged?ad_id=${ad._id}&event_type=click&name=${encodeURIComponent(ad.business_name || '')}`)}
                                            >
                                                {ad.clicks} clicks
                                            </div>
                                            <div className="ad-stats__row-stat" style={{ color: ad.ctr > 5 ? '#2ECC71' : ad.ctr > 0 ? '#F1C40F' : 'rgba(255,255,255,0.3)' }}>
                                                {ad.ctr}% CTR
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <TabsContainer className="mb-2">
                            <Tab active={activeTab === 'pending'} onClick={() => setActiveTab('pending')}>
                                Pending {pendingAds.length > 0 ? `(${pendingAds.length})` : ''}
                            </Tab>
                            <Tab active={activeTab === 'current'} onClick={() => setActiveTab('current')}>
                                Current
                            </Tab>
                            <Tab active={activeTab === 'past'} onClick={() => setActiveTab('past')}>
                                Past
                            </Tab>
                        </TabsContainer>

                        {activeTab === 'pending' && (
                            pendingLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <Skeleton type="row" /><Skeleton type="row" />
                                </div>
                            ) : pendingAds.length === 0 ? (
                                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', padding: 60 }}>No ads pending review</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {pendingAds.map(ad => (
                                        <div key={ad._id} className="ad-stats__breakdown" style={{ padding: 16 }}>
                                            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
                                                {ad.photo_url && <img src={ad.photo_url} alt="" style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover' }} />}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{ad.business_name}</div>
                                                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{ad.website || 'No website'}</div>
                                                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{formatDate(ad.date_start)} → {formatDate(ad.date_end)}</div>
                                                </div>
                                            </div>
                                            {ad.about && <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 16, lineHeight: 1.5 }}>{ad.about}</div>}
                                            <div style={{ display: 'flex', gap: 10 }}>
                                                <Button primaryBtn onClick={() => handleApprove(ad._id)}>Approve</Button>
                                                <button onClick={() => handleReject(ad._id)} className="btn btn-outline-danger">Reject</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                        {activeTab === 'current' && <CurrentAdvertising />}
                        {activeTab === 'past' && <PastAdvertising />}
                    </section>
                </article>
            </div>
        </main>
    );
}

export default AdManagement;
